import { useHistory } from 'react-router-dom';
import React, { useEffect, useRef } from 'react';
import { styled } from 'linaria/react';
import { cx } from 'linaria';
import t from '@jetshop/intl';
import Accordion from '@jetshop/ui/Accordion/Accordion';
import Drawer from '@jetshop/ui/Modal/Drawer';
import { ReactComponent as CloseIcon } from '../../svg/Cross.svg';
import { hasData, isFullySelected } from '../../utils/modelFilter';
import { ClearButton } from '../CategoryPage/Filters/ActiveFilters';
import { theme } from '../Theme';
import { FilterCategory } from './FilterCategory';
import { MODEL_FILTER_TYPES } from './constants';
import { useModelFilterSelectionData } from './useModelFilterSelectionData';
import CategoryLink from '@jetshop/ui/CategoryLink';
import { buttonBaseCSS, buttonTertiary } from '../ui/Button';
import { useIsModelFilterRoute } from './ModelFilterButton';

const FILTER_TYPES_ORDER = [
  MODEL_FILTER_TYPES.TYPE,
  MODEL_FILTER_TYPES.BRAND,
  MODEL_FILTER_TYPES.MODEL
];

const TopBar = styled('div')`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 16px;
  height: 56px;
`;

const CloseBtn = styled('button')`
  position: absolute;
  left: 0.375rem;
  background: none;
  border: none;
  cursor: pointer;
  width: 0.75rem;
  height: 0.75rem;
  padding: 1rem;
  box-sizing: content-box;
  display: flex;
  align-items: center;
  justify-content: center;

  g {
    stroke: ${theme.colors.darkgrey} !important;
  }
`;

const Title = styled('h1')`
  margin: 0;
  padding: 0;
  font-size: 1rem;
  color: ${theme.colors.darkgrey};
  text-align: center;
  flex: 1;
`;

const ButtonContainer = styled('div')`
  padding: 0 ${theme.space[2]};

  & + & {
    margin-top: ${theme.space[2]};
  }
`;

const ModelFilterLink = styled(CategoryLink)`
  width: 100%;

  &:hover {
    color: white !important;
    text-decoration: none !important;
  }
`;

export function ModelFilterDrawer({ isOpen, onClose }) {
  const {
    modelFilters,
    getFilterItems,
    toggleFilterItem,
    loadInitialData,
    loading,
    clear: clearModelFilters
  } = useModelFilterSelectionData();
  const isModellfilterRoute = useIsModelFilterRoute();

  const firstOpenRef = useRef(false);
  const accordionPropsRef = useRef(null);
  const desiredNextIdxRef = useRef(null);

  const history = useHistory();

  // initial load
  useEffect(() => {
    if (isOpen && !firstOpenRef.current) {
      firstOpenRef.current = true;
      loadInitialData();
    }
  }, [isOpen, loadInitialData]);

  // once loading finishes, open the next panel if requested
  useEffect(() => {
    if (!loading && desiredNextIdxRef.current !== null) {
      const idx = desiredNextIdxRef.current;
      const items = getFilterItems(FILTER_TYPES_ORDER[idx]);
      if (items?.length) {
        accordionPropsRef.current.handleClick(idx);
        desiredNextIdxRef.current = null;
      }
    }
  }, [loading, getFilterItems]);

  return (
    <Drawer isOpen={isOpen} right>
      <TopBar>
        <CloseBtn onClick={onClose}>
          <CloseIcon />
        </CloseBtn>
        <Title>{t('Model filters')}</Title>
      </TopBar>

      <section>
        <Accordion single>
          {accordionProps => {
            // capture accordion API
            accordionPropsRef.current = accordionProps;

            const handleItemToggleWithNav = (
              type,
              id,
              text,
              idx,
              currentSelected
            ) => {
              const didSelect = !currentSelected?.some(item => item.id === id);

              toggleFilterItem(type, id, text);

              if (!didSelect) {
                return;
              }

              const nextIdx = idx + 1;
              if (nextIdx >= FILTER_TYPES_ORDER.length) {
                if (!isModellfilterRoute) {
                  history.push('/modellfilter');
                }
                onClose();
                return;
              }
              // request opening of the next panel once data loads
              desiredNextIdxRef.current = nextIdx;
            };

            return (
              <>
                <FilterCategory
                  title={t('Type')}
                  idx={0}
                  filterType={MODEL_FILTER_TYPES.TYPE}
                  getItems={() => getFilterItems(MODEL_FILTER_TYPES.TYPE)}
                  selectedItems={modelFilters?.type}
                  onItemToggle={(t, id, text) =>
                    handleItemToggleWithNav(t, id, text, 0, modelFilters?.type)
                  }
                  accordionProps={accordionProps}
                />
                <FilterCategory
                  title={t('Brand')}
                  idx={1}
                  filterType={MODEL_FILTER_TYPES.BRAND}
                  getItems={() => getFilterItems(MODEL_FILTER_TYPES.BRAND)}
                  selectedItems={modelFilters?.brand}
                  onItemToggle={(t, id, text) =>
                    handleItemToggleWithNav(t, id, text, 1, modelFilters?.brand)
                  }
                  accordionProps={accordionProps}
                />
                <FilterCategory
                  title={t('Model')}
                  idx={2}
                  filterType={MODEL_FILTER_TYPES.MODEL}
                  getItems={() => getFilterItems(MODEL_FILTER_TYPES.MODEL)}
                  selectedItems={modelFilters?.model}
                  onItemToggle={(t, id, text) =>
                    handleItemToggleWithNav(t, id, text, 2, modelFilters?.model)
                  }
                  accordionProps={accordionProps}
                />
              </>
            );
          }}
        </Accordion>
      </section>

      {hasData(modelFilters) && (
        <ButtonContainer>
          <ClearButton
            className={cx(buttonBaseCSS, buttonTertiary)}
            onClick={() => clearModelFilters()}
            disabled={loading}
          >
            {t('Clear all filters')}
          </ClearButton>
        </ButtonContainer>
      )}
      {!isModellfilterRoute && isFullySelected(modelFilters) && (
        <ButtonContainer>
          <ModelFilterLink
            className={cx(buttonBaseCSS)}
            category={{
              primaryRoute: {
                id: 'modellfilter',
                path: '/modellfilter'
              }
            }}
            onClick={() => {
              onClose();
              return;
            }}
          >
            {t('Visit model filter')}
          </ModelFilterLink>
        </ButtonContainer>
      )}
    </Drawer>
  );
}
